import { Form } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  type ModelItem,
  useLazyGetPositionByIdQuery,
  useLazyGetPositionRolesQuery,
  useUpdatePositionMutation,
} from '@app/api/Positions';
import { RIGHTS_IS_EDIT, RIGHTS_POSITIONS } from '@app/constants/accessRights';
import { ERROR, SUCCESS, WARNING } from '@app/constants/alerts';
import { useAccessRights, useUnsavedData, useUserPrint } from '@app/hooks';
import { LeavePageContext, NotificationContext } from '@app/providers';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import { handleCatchError } from '@app/services';
import type { NPositions } from '@app/types/Positions';

import type { NAddPosition } from '@pages/Employees/AddPosition/AddPosition.types';

const { useForm, useWatch } = Form;

export const useAddPosition = () => {
  const { activeBranchId } = useUserPrint();
  const { hasUserRight: hasUserRightToEditPosition } = useAccessRights(
    RIGHTS_POSITIONS,
    RIGHTS_IS_EDIT
  );

  const [isLoading, setIsLoading] = useState(false);
  const [form] = useForm();
  const [serverError, setServerError] = useState('');
  const [position, setPosition] = useState<NPositions.ModelPosition>();
  const [modalOpen, setModalOpen] = useState(false);
  const [roles, setRoles] = useState<ModelItem[]>([]);

  const [searchParams] = useSearchParams();
  const editablePositionId = Number(searchParams.get('id'));

  const { showNotification } = useContext(NotificationContext);

  const { prepareStartedData, checkIsDataUpdated, setInputData } =
    useContext(LeavePageContext);

  const navigate = useNavigate();

  const [getPositionById] = useLazyGetPositionByIdQuery();
  const [getPositionRoles] = useLazyGetPositionRolesQuery();
  const [updatePosition] = useUpdatePositionMutation();

  useEffect(() => {
    const fetchPositionRoles = async () => {
      try {
        const result = await getPositionRoles({
          ActiveBranchId: activeBranchId,
        }).unwrap();

        setRoles(result);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    fetchPositionRoles();
  }, [activeBranchId, getPositionRoles, showNotification]);

  useEffect(() => {
    const fetchPositionData = async () => {
      if (editablePositionId) {
        setIsLoading(true);
        try {
          const result = await getPositionById({
            ActiveBranchId: activeBranchId,
            positionId: editablePositionId,
          }).unwrap();

          form.setFieldsValue({
            Name: result.Name,
            Description: result.Description,
            isDeleted: result.IsDeleted,
            RoleKeys:
              result.Roles?.map(({ IdS }) => IdS).filter((idS): idS is string =>
                Boolean(idS)
              ) ?? [],
          });

          setPosition(result);
          prepareStartedData(result);
        } catch (error) {
          const message = error instanceof Error ? error.message : '';
          const cause = error instanceof Error ? (error.cause as string) : '';
          showNotification(message, cause as NNotificationProvider.NotifyType);
        } finally {
          setIsLoading(false);
        }
      } else {
        prepareStartedData({
          Name: undefined,
          Description: undefined,
          RoleKeys: [],
        });
      }
    };

    fetchPositionData();
  }, [
    activeBranchId,
    editablePositionId,
    form,
    showNotification,
    prepareStartedData,
    getPositionById,
  ]);

  const observedData = useWatch([], form);

  const { saveData } = useUnsavedData({
    data: observedData,
    skip: isLoading || !Boolean(editablePositionId),
  });

  const roleByKey = useMemo(() => {
    const map = new Map<string, ModelItem>();

    [...roles, ...(position?.Roles ?? [])].forEach((role) => {
      if (role.IdS !== null && !map.has(role.IdS)) {
        map.set(role.IdS, role);
      }
    });

    return map;
  }, [roles, position]);

  const submitForm: NAddPosition.SubmitForm = useCallback(
    async ({ Name, Description, isDeleted, RoleKeys }) => {
      setIsLoading(true);
      try {
        await updatePosition({
          ActiveBranchId: activeBranchId,
          data: {
            Id: position ? position.Id : 0,
            Name,
            Description,
            IsDeleted: isDeleted,
            MigrateId: position ? position.MigrateId : null,
            Roles: RoleKeys.map((key) => roleByKey.get(key)).filter(
              (role): role is ModelItem => Boolean(role)
            ),
          },
        }).unwrap();

        saveData();
        showNotification('Должность обновлена успешно', SUCCESS);
        navigate('/positions');
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        const cause = error instanceof Error ? (error.cause as string) : '';

        if (cause === ERROR) {
          showNotification(message, cause);
        } else if (cause === WARNING) {
          setServerError(message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeBranchId,
      navigate,
      showNotification,
      position,
      roleByKey,
      updatePosition,
      saveData,
    ]
  );

  const deletePosition = async () => {
    setIsLoading(true);
    try {
      await updatePosition({
        ActiveBranchId: activeBranchId,
        data: {
          Id: position ? position.Id : 0,
          Name: form.getFieldValue('Name'),
          Description: form.getFieldValue('Description'),
          IsDeleted: true,
          MigrateId: position ? position.MigrateId : null,
          ...(position?.Roles && { Roles: position.Roles }),
        },
      }).unwrap();
      showNotification('Должность удалена успешно', SUCCESS);
      navigate('/positions');
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      const cause = error instanceof Error ? (error.cause as string) : '';
      if (cause === ERROR) {
        showNotification(message, cause);
      } else if (cause === WARNING) {
        setServerError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    checkIsDataUpdated({
      path: '/positions',
      navigate,
    });
  };

  const initialValues = useMemo(() => {
    const {
      Name = '',
      Description = '',
      isDeleted = false,
      RoleKeys = [],
    } = form.getFieldsValue();
    return {
      Name,
      Description,
      isDeleted,
      RoleKeys,
    };
  }, [form]);

  const roleOptions = useMemo(
    () =>
      roles
        .filter(({ IdS }) => IdS !== null)
        .map(({ IdS, Name }) => ({ label: Name, value: IdS })),
    [roles]
  );

  return {
    isLoading,
    form,
    submitForm,
    serverError,
    setServerError,
    editablePositionId,
    modalOpen,
    setModalOpen,
    handleCancel,
    deletePosition,
    hasUserRightToEditPosition,
    setInputData,
    initialValues,
    roleOptions,
  };
};
